/**
 * Offboarding API hooks — templates + checklist assignments.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type {
  OffboardingAssignment,
  OffboardingTemplate,
  Paginated,
} from '@shared/types';
import {
  offboardingAssignInput,
  offboardingTemplateCreateInput,
  offboardingTemplateUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type OffboardingTemplateCreateBody = z.input<typeof offboardingTemplateCreateInput>;
export type OffboardingTemplateUpdateBody = z.input<typeof offboardingTemplateUpdateInput>;
export type OffboardingAssignBody = z.input<typeof offboardingAssignInput>;

export const offboardingKeys = {
  all: ['offboarding'] as const,
  templates: (params?: { activeOnly?: boolean }) =>
    [...offboardingKeys.all, 'templates', params ?? {}] as const,
  template: (id: string) => [...offboardingKeys.all, 'template', id] as const,
  assignments: (params?: { employeeId?: string; status?: string }) =>
    [...offboardingKeys.all, 'assignments', params ?? {}] as const,
  assignment: (id: string) => [...offboardingKeys.all, 'assignment', id] as const,
};

export function useOffboardingTemplates(params?: { activeOnly?: boolean }) {
  return useQuery({
    queryKey: offboardingKeys.templates(params),
    queryFn: () =>
      api.get<Paginated<OffboardingTemplate>>(
        `/api/offboarding/templates${qs({
          activeOnly: params?.activeOnly ? '1' : undefined,
          limit: 100,
        })}`,
      ),
  });
}

export function useCreateOffboardingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OffboardingTemplateCreateBody) =>
      api.post<OffboardingTemplate>('/api/offboarding/templates', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offboardingKeys.all });
    },
  });
}

export function useUpdateOffboardingTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: OffboardingTemplateUpdateBody }) =>
      api.put<OffboardingTemplate>(`/api/offboarding/templates/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offboardingKeys.all });
    },
  });
}

export function useOffboardingAssignments(params?: { employeeId?: string; status?: string }) {
  return useQuery({
    queryKey: offboardingKeys.assignments(params),
    queryFn: () =>
      api.get<Paginated<OffboardingAssignment>>(
        `/api/offboarding/assignments${qs({
          employeeId: params?.employeeId,
          status: params?.status,
          limit: 100,
        })}`,
      ),
  });
}

export function useOffboardingAssignment(id: string | null) {
  return useQuery({
    queryKey: offboardingKeys.assignment(id ?? ''),
    enabled: !!id,
    queryFn: () => api.get<OffboardingAssignment>(`/api/offboarding/assignments/${id}`),
  });
}

export function useAssignOffboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OffboardingAssignBody) =>
      api.post<OffboardingAssignment>('/api/offboarding/assignments', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offboardingKeys.all });
    },
  });
}

export function useCompleteOffboardingItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      itemId,
      undo,
    }: {
      assignmentId: string;
      itemId: string;
      undo?: boolean;
    }) =>
      api.post<OffboardingAssignment>(
        `/api/offboarding/assignments/${assignmentId}/items/${itemId}/complete${undo ? '?undo=1' : ''}`,
        {},
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: offboardingKeys.all });
      void queryClient.invalidateQueries({
        queryKey: offboardingKeys.assignment(vars.assignmentId),
      });
    },
  });
}

export function useCancelOffboardingAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<OffboardingAssignment>(`/api/offboarding/assignments/${id}/cancel`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offboardingKeys.all });
    },
  });
}
